import { useRef, useState } from 'react'
import { useOverflowFocus } from '@/hooks/use-overflow-focus'
import { AlertTriangle, CheckCircle2, Loader2, Upload as UploadIcon } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import {
  useBillingsFiscalYears,
  useBillingsImports,
  useCommitBillingsImport,
  usePreviewBillingsImport,
} from '@/hooks/use-api'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { explainError } from '@/lib/explain-error'
// formatDate, not toLocaleDateString: a DATE-only value formatted through a Date
// shifts across timezones, which is the same hazard the importer guards against.
import { formatDate } from '@/lib/utils'
import { WipeFiscalYearDialog } from '@/components/sales-trackers/WipeFiscalYearDialog'
import { LegacyWorkbookUpload } from '@/components/sales-trackers/LegacyWorkbookUpload'
import type { BillingsImport, BillingsPreview } from '@/types'

/**
 * Upload a QuickBooks Net Billings export.
 *
 * Two-phase: the file is parsed and reported first, and nothing is written until
 * the preview is committed. That matters more here than on a normal import because
 * the two exports use OPPOSITE sign conventions, and the preview is where a person
 * can see that the CRM read the file the way they expect.
 *
 * The company is detected from the report's own entity line, not the filename, so
 * a renamed file still lands correctly.
 */
export default function SalesTrackersUpload() {
  const history = useOverflowFocus('History of QuickBooks billings uploads')
  useDocumentTitle('Upload QuickBooks billings')

  const { toast } = useToast()
  const fileRef = useRef<HTMLInputElement>(null)

  const [preview, setPreview] = useState<BillingsPreview | null>(null)
  const [fxRate, setFxRate] = useState('')
  const [fxNote, setFxNote] = useState('')
  const [error, setError] = useState('')
  const [status, setStatus] = useState('')

  const previewMutation = usePreviewBillingsImport()
  const commitMutation = useCommitBillingsImport()
  const imports = useBillingsImports()
  const years = useBillingsFiscalYears()
  const [wiping, setWiping] = useState(false)

  const money = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 2 })

  const handlePreview = async () => {
    const file = fileRef.current?.files?.[0]
    if (!file) {
      setError('Choose a file first.')
      return
    }
    setError('')
    setPreview(null)
    setStatus('Reading the file. A large export can take a minute.')
    try {
      const result = await previewMutation.mutateAsync({
        file,
        fxToUsd: fxRate || undefined,
        fxNote: fxNote || undefined,
      })
      setPreview(result)
      setStatus(
        `${result.total_rows.toLocaleString()} rows read from ${result.entity_label ?? 'the export'}. Nothing has been saved yet.`
      )
    } catch (err) {
      setError(explainError(err, 'Could not read that file.'))
      setStatus('')
    }
  }

  const handleCommit = async () => {
    if (!preview) return
    try {
      const result = await commitMutation.mutateAsync(preview.preview_token)
      toast({
        title: 'Billings saved',
        description:
          `${result.rows_written.toLocaleString()} lines from ${result.source}. ` +
          `${result.new_rows.toLocaleString()} new, ${result.refreshed_rows.toLocaleString()} refreshed.`,
      })
      setPreview(null)
      setStatus('')
      if (fileRef.current) fileRef.current.value = ''
    } catch (err) {
      setError(explainError(err, 'The upload could not be saved.'))
    }
  }

  return (
    <section className="p-6 space-y-6" aria-labelledby="sales-trackers-heading">
      <div>
        <h1 id="sales-trackers-heading" className="text-2xl font-semibold">
          Upload QuickBooks billings
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Drop in the Net Billings export from QuickBooks, one company at a time. Uploading the same
          period again refreshes it rather than duplicating anything, so it is always safe to
          repeat.
        </p>
      </div>

      {(imports.data?.rateIssues.length ?? 0) > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>Some sales are recorded at the wrong exchange rate</AlertTitle>
          <AlertDescription>
            <p>
              These rows were imported before the rate became automatic, so they were never
              converted. The rate is frozen once a line is saved, which means re-uploading the same
              file will <span className="font-medium">not</span> fix them: it restores the row and
              keeps its original rate. Wiping the year and re-uploading is the only route.
            </p>
            <ul className="mt-2 space-y-1">
              {imports.data?.rateIssues.map((r) => (
                <li key={`${r.source_entity}-${r.fiscal_year}-${r.recorded_rate}`}>
                  <span className="font-medium">
                    FY{r.fiscal_year} {r.source_entity}
                  </span>
                  : {r.line_count.toLocaleString()} lines at {r.recorded_rate} instead of{' '}
                  {r.expected_rate}. Reads {money(r.amount)}; would read{' '}
                  {money(r.amount_at_expected_rate)}.
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Monthly upload</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <Label htmlFor="billings-file">QuickBooks export</Label>
              <Input
                id="billings-file"
                type="file"
                ref={fileRef}
                accept=".xlsx,.csv"
                aria-describedby="billings-file-help"
                disabled={previewMutation.isPending}
              />
              <p id="billings-file-help" className="text-xs text-muted-foreground mt-1">
                Net Billings export, .xlsx or .csv. The company is read from the report itself, so
                the filename does not matter.
              </p>
            </div>
            <div>
              <Label htmlFor="fx-rate">Rate to CAD</Label>
              <Input
                id="fx-rate"
                type="number"
                step="0.0001"
                min="0"
                placeholder="leave blank"
                value={fxRate}
                onChange={(e) => setFxRate(e.target.value)}
                aria-describedby="fx-help"
                disabled={previewMutation.isPending}
              />
              <p id="fx-help" className="text-xs text-muted-foreground mt-1">
                Leave blank to keep the rate this company already uses. Only type one to change it
                deliberately. GrackleDocs Inc. bills in CAD, so its rate is 1; the Australian entity
                bills in AUD, so an AU month reads about 10% below the raw workbook column once
                converted.
              </p>
            </div>
          </div>

          <div>
            <Label htmlFor="fx-note">Rate note (optional)</Label>
            <Input
              id="fx-note"
              value={fxNote}
              onChange={(e) => setFxNote(e.target.value)}
              placeholder="e.g. Bank of Canada, 1 Aug 2026"
              disabled={previewMutation.isPending}
            />
          </div>

          <div className="flex items-center gap-3">
            <Button
              type="button"
              onClick={handlePreview}
              disabled={previewMutation.isPending}
              aria-label="Read the file and show what will be imported"
            >
              {previewMutation.isPending ? (
                <Loader2
                  className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none"
                  aria-hidden="true"
                />
              ) : (
                <UploadIcon className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              {previewMutation.isPending ? 'Reading' : 'Read file'}
            </Button>
            <span className="text-sm text-muted-foreground">Nothing is saved at this step.</span>
          </div>

          <p role="status" aria-live="polite" className="text-sm text-muted-foreground">
            {status}
          </p>

          {error && (
            <Alert variant="destructive">
              <AlertDescription role="alert">{error}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {preview && (
        <Card>
          <CardHeader>
            <CardTitle>What will be imported</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 text-sm">
              <Field label="Company" value={preview.entity_label ?? '—'} />
              <Field label="Period in the report" value={preview.period_label ?? '—'} />
              <Field
                label="Dates found"
                value={
                  preview.date_range
                    ? `${preview.date_range.from} to ${preview.date_range.to}`
                    : '—'
                }
              />
              <Field label="Rows" value={preview.total_rows.toLocaleString()} />
              <Field label="New" value={preview.new_rows.toLocaleString()} />
              <Field label="Already present" value={preview.refreshed_rows.toLocaleString()} />
              <Field label="Total after correction" value={money(preview.normalised_total)} />
              <Field
                label="Sign handling"
                value={preview.sign_multiplier === -1 ? 'Flipped' : 'Unchanged'}
              />
            </dl>

            {/* The sign convention is the single thing most likely to be misread,
                so it is stated in words rather than left implied by a multiplier.
                It is now READ FROM THE FILE rather than assumed from the company,
                and a disagreement is raised loudly: applying the usual convention
                to a file that does not follow it turns revenue negative. */}
            <Alert
              variant={preview.sign_unusual ? 'destructive' : undefined}
              role={preview.sign_unusual ? 'alert' : 'note'}
            >
              <AlertTitle>
                {preview.sign_unusual
                  ? 'This file does not follow the usual convention'
                  : 'How the amounts were read'}
              </AlertTitle>
              <AlertDescription>
                {preview.sign_note}
                {preview.sign_basis && (
                  <span className="block mt-1 text-xs">{preview.sign_basis}</span>
                )}
              </AlertDescription>
            </Alert>

            {/* The sign DOES reach rows that are already here, unlike the rate.
                That is deliberate - it is the only way to repair a file imported
                under the wrong convention - but it moves figures that have already
                been reported, so it is said out loud beforehand. */}
            {preview.sign_corrects_existing_rows && (
              <Alert role="alert">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                <AlertTitle>
                  This will correct the sign on {preview.refreshed_rows.toLocaleString()} rows
                  already saved
                </AlertTitle>
                <AlertDescription>
                  Those rows were read as{' '}
                  {preview.existing_signs
                    .map((s) => (s === -1 ? 'flipped' : 'unchanged'))
                    .join(' / ')}{' '}
                  and will now be read as {preview.sign_multiplier === -1 ? 'flipped' : 'unchanged'}
                  . Months you have already reported will change. That is the intended way to fix an
                  upload that came in the wrong way round, so check the total below is the one you
                  expect.
                </AlertDescription>
              </Alert>
            )}

            {/* A split is the failure the per-line rate makes possible: one entity
                holding two rates, a 10% error on a subset, absent from every total
                because each row carries its own. Say it before it happens. */}
            {preview.rate_split_warning && (
              <Alert variant="destructive" role="alert">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                <AlertTitle>
                  {preview.source} would end up with more than one exchange rate
                </AlertTitle>
                <AlertDescription>
                  After this upload, {preview.source} rows would be converted at{' '}
                  {preview.entity_rates_after_import.join(' and ')}. Two rates inside one company
                  means a subset of rows is worth 10% more or less than its neighbours, and no total
                  will show it. Use the rate the existing rows already use, unless you mean to
                  change it.
                </AlertDescription>
              </Alert>
            )}

            {preview.fx_to_usd_basis === 'entity-default' && (
              <Alert role="note">
                <AlertTitle>
                  Converting at {preview.fx_to_usd} &mdash; {preview.source}&rsquo;s standard rate
                </AlertTitle>
                <AlertDescription>
                  {preview.source === 'AU'
                    ? `This is an Australian export, so amounts are AUD and are converted to CAD at ${preview.fx_to_usd}. Nothing needs typing. Enter a rate above only for a month that genuinely used a different one.`
                    : `GrackleDocs Inc. bills in CAD, so no conversion is applied. Enter a rate above only if this month genuinely needs one.`}
                </AlertDescription>
              </Alert>
            )}

            {(preview.fx_to_usd_basis === 'inherited' ||
              preview.fx_to_usd_basis === 'inherited-majority') && (
              <Alert role="note">
                <AlertTitle>Using the rate {preview.source} already uses</AlertTitle>
                <AlertDescription>
                  No rate was entered and {preview.source} has no standard rate, so{' '}
                  {preview.fx_to_usd} was taken from the rows already saved for it rather than
                  defaulting to 1. Entering a rate overrides this.
                </AlertDescription>
              </Alert>
            )}

            {preview.overrides_cleared_kept > 0 && (
              <Alert role="note">
                <AlertTitle>
                  {preview.overrides_cleared_kept.toLocaleString()} attribution
                  {preview.overrides_cleared_kept === 1 ? '' : 's'} you cleared will stay cleared
                </AlertTitle>
                <AlertDescription>
                  Clearing an attribution is a decision made here, so an upload does not undo it. To
                  put one back, set it again on the Attribution screen.
                </AlertDescription>
              </Alert>
            )}

            {/* Otherwise a person entering a new rate reasonably expects it to
                apply to everything, and would not know that months already
                reported keep their original rate. */}
            {preview.rate_applies_to_new_rows_only && (
              <Alert role="note">
                <AlertTitle>The new rate applies to new rows only</AlertTitle>
                <AlertDescription>
                  {preview.refreshed_rows.toLocaleString()} of these rows are already here and keep
                  the rate they were imported at
                  {preview.existing_rates.length > 0
                    ? ` (${preview.existing_rates.join(', ')})`
                    : ''}
                  , so months you have already reported will not change. Only the{' '}
                  {preview.new_rows.toLocaleString()} new rows use {preview.fx_to_usd}. The sign is
                  handled the other way round: it is refreshed on every upload, so a file that came
                  in the wrong way round can be corrected by uploading it again.
                </AlertDescription>
              </Alert>
            )}

            {preview.overrides_preserved > 0 && (
              <Alert role="note">
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                <AlertTitle>
                  {preview.overrides_preserved.toLocaleString()} manual rep corrections will be kept
                </AlertTitle>
                <AlertDescription>
                  Corrections are matched on the content of a line, so re-uploading does not undo
                  them.
                </AlertDescription>
              </Alert>
            )}

            {preview.unmapped_reps.length > 0 && (
              <Alert role="note">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                <AlertTitle>
                  {preview.unmapped_reps.length} rep{preview.unmapped_reps.length === 1 ? '' : 's'}{' '}
                  not recognised
                </AlertTitle>
                <AlertDescription>
                  These lines will be counted in the totals but attributed to nobody until they are
                  mapped.
                  <ul className="mt-2 space-y-0.5">
                    {preview.unmapped_reps.slice(0, 6).map((r) => (
                      <li key={r.value}>
                        <span className="font-medium">{r.value}</span> — {r.rows} rows,{' '}
                        {money(r.amount)}
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {preview.unclassified_accounts.length > 0 && (
              <Alert role="note">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                <AlertTitle>
                  {preview.unclassified_accounts.length} sales account
                  {preview.unclassified_accounts.length === 1 ? '' : 's'} not categorised
                </AlertTitle>
                <AlertDescription>
                  These count toward the total but appear in neither Software nor Service.
                  <ul className="mt-2 space-y-0.5">
                    {preview.unclassified_accounts.map((a) => (
                      <li key={a.value}>
                        <span className="font-medium">{a.value}</span> — {a.rows} rows,{' '}
                        {money(a.amount)}
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {preview.skipped_rows.length > 0 && (
              <Alert role="note">
                <AlertTitle>
                  {preview.skipped_rows.length} row
                  {preview.skipped_rows.length === 1 ? '' : 's'} skipped
                </AlertTitle>
                <AlertDescription>
                  Usually a total or subtotal line with no date. Reported here rather than dropped
                  quietly.
                  <ul className="mt-2 space-y-0.5">
                    {preview.skipped_rows.slice(0, 5).map((s) => (
                      <li key={s.row_number}>
                        Row {s.row_number}: {s.reason}
                        {s.customer ? ` — ${s.customer}` : ''}
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <div className="flex items-center gap-3 pt-2 border-t">
              <Button
                type="button"
                onClick={handleCommit}
                disabled={commitMutation.isPending}
                aria-label="Save these billings"
              >
                {commitMutation.isPending && (
                  <Loader2
                    className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                )}
                {commitMutation.isPending ? 'Saving' : 'Save billings'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setPreview(null)
                  setStatus('')
                }}
                disabled={commitMutation.isPending}
              >
                Discard
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* The other route in: the workbook itself. Jason uploads whichever he has,
          so both live on this page rather than one being a hidden endpoint. */}
      {/* A one-time history import, not part of the monthly routine, so it is folded
          away by default. Two cards of equal weight read as a choice to be made every
          month, which is not what this is: Jason works from the QuickBooks export, and
          the workbook exists to bring the past across once. */}
      <details className="rounded-md border bg-muted/20">
        <summary className="cursor-pointer px-4 py-3 text-sm font-medium marker:text-muted-foreground">
          One-time setup: import history from the Sales Tracker workbook
          <span className="block text-xs font-normal text-muted-foreground mt-0.5">
            Only needed to bring past years and your existing rep corrections into the CRM. Not part
            of the monthly upload.
          </span>
        </summary>
        <div className="px-4 pb-4">
          <LegacyWorkbookUpload />
        </div>
      </details>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
          <CardTitle>Previous uploads</CardTitle>
          {/* Sits beside the history rather than near the upload form: it is the thing
              you reach for when a year already in the list is wrong, not part of the
              monthly routine. */}
          {(years.data?.length ?? 0) > 0 && (
            <Button variant="outline" size="sm" onClick={() => setWiping(true)}>
              Wipe a fiscal year
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {imports.data && imports.data.imports.length > 0 ? (
            <div className="overflow-x-auto" ref={history.ref} {...history.regionProps}>
              <table className="w-full text-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                <caption className="sr-only">History of QuickBooks billings uploads</caption>
                <thead>
                  <tr className="border-b">
                    <th scope="col" className="text-left font-medium py-2 pr-3">
                      When
                    </th>
                    <th scope="col" className="text-left font-medium py-2 pr-3">
                      Company
                    </th>
                    <th scope="col" className="text-left font-medium py-2 pr-3">
                      File
                    </th>
                    <th scope="col" className="text-right font-medium py-2 px-3">
                      Rows
                    </th>
                    <th scope="col" className="text-right font-medium py-2 px-3">
                      Rate
                    </th>
                    <th scope="col" className="text-left font-medium py-2 pl-3">
                      By
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {imports.data.imports.map((imp: BillingsImport) => (
                    <tr key={imp.id} className="border-b last:border-0">
                      <td className="py-1.5 pr-3 whitespace-nowrap">
                        {formatDate(imp.created_date)}
                      </td>
                      <td className="py-1.5 pr-3">{imp.source_entity}</td>
                      <th scope="row" className="text-left font-normal py-1.5 pr-3">
                        {imp.file_name}
                      </th>
                      <td className="text-right py-1.5 px-3">{imp.row_count.toLocaleString()}</td>
                      {/* The entered rate is not always the rate in force: refreshed
                          rows keep the one they arrived at. Showing only the entered
                          one made the audit trail state a rate none of its rows used. */}
                      <td className="text-right py-1.5 px-3">
                        {imp.rate_entered}
                        {imp.rate_differs_from_rows && (
                          <span className="block text-xs text-muted-foreground">
                            rows use {imp.line_rates.join(', ')}
                          </span>
                        )}
                      </td>
                      <td className="py-1.5 pl-3">{imp.uploaded_by ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No uploads yet.</p>
          )}
        </CardContent>
      </Card>

      <WipeFiscalYearDialog open={wiping} onOpenChange={setWiping} years={years.data ?? []} />
    </section>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium mt-0.5">{value}</dd>
    </div>
  )
}
